import React, { useState } from 'react';
import { Typography, Button, Theme, Switch, Tooltip, Grid2, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
// import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useKrispToggle } from '../../../hooks/useKrispToggle/useKrispToggle';
import SmallCheckIcon from '../../../icons/SmallCheckIcon';
import InfoIconOutlined from '../../../icons/InfoIconOutlined';
import AvataraLogo from '../../IntroContainer/AvataraLogo';
import AvataraModal from '../../AvataraModal/AvataraModal';
import Snackbar from '../../Snackbar/Snackbar';
import RoomOptions from './RoomOptions/RoomOptions';
import InviteDialog from '../../InviteDialog/InviteDialog';
import UserAndRoomDetails from './UserDetails/UserAndRoomDetails';
import { capitalize } from '../../../utils';
import { RoomPrivacyType } from '../../../types';

const useStyles = makeStyles((theme: Theme) => ({
  deviceButton: {
    width: '100%',
    border: '2px solid #aaa',
    '&:not(:first-child)': {
      marginTop: '1em',
    },
  },
}));

export default function DeviceSelectionScreen() {
  const classes = useStyles();
  const { isFetching, isKrispEnabled, isKrispInstalled, password, roomName, username, setUsername } = useAppState();
  // const { connect: chatConnect } = useChatContext();
  const {
    isAcquiringLocalTracks,
    isConnecting,
    isJoining,
    isWaitingForHostToCreateRoom,
    showRequestRejectedSnackbar,
    setShowRequestRejectedSnackbar,
    handleJoinRoom,
  } = useVideoContext();
  const { toggleKrisp } = useKrispToggle();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting || isJoining;
  const [manualUsername, setManualUsername] = useState(username);
  const [usernameError, setUsernameError] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [shouldRecord, setShouldRecord] = useState(false);
  const [roomPrivacy, setRoomPrivacy] = useState<RoomPrivacyType>('public');

  /**
   * Handles the join button click, performs various checks and joins the room
   */
  const handleJoinClick = async () => {
    if (!username && !manualUsername) {
      setUsernameError(true);
      return;
    }
    const name = username || capitalize(manualUsername);
    if (!username) {
      setUsername(manualUsername);
    }

    handleJoinRoom({ roomName, username: name, privacy: roomPrivacy, shouldRecord });
  };

  const handleManualUsernameChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = e => {
    const { value } = e.target;
    setUsernameError(false);
    setManualUsername(value);
  };

  if (isJoining || isFetching || isConnecting) {
    return (
      <Grid2 container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid2>
    );
  }

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <AvataraLogo />
      </Box>

      {/* <FilesProvider>
        <Files />
      </FilesProvider> */}

      <AvataraModal
        open={isWaitingForHostToCreateRoom}
        handleClose={() => console.log('closing waiting modal')}
        children={<Typography variant="h6">Waiting for the host to create the room...</Typography>}
      />

      <Snackbar
        open={showRequestRejectedSnackbar}
        headline=""
        handleClose={() => setShowRequestRejectedSnackbar(false)}
        message="Request Rejected!"
        variant="error"
        horizontal="center"
      />

      <Grid2 container spacing={1} sx={{ mb: 2 }}>
        <UserAndRoomDetails
          manualUsername={manualUsername}
          roomName={roomName}
          username={username}
          usernameError={usernameError}
          handleManualUsernameChange={handleManualUsernameChange}
        />
        {password && (
          <RoomOptions
            roomPrivacy={roomPrivacy}
            shouldRecord={shouldRecord}
            setRoomPrivacy={setRoomPrivacy}
            setShouldRecord={setShouldRecord}
          />
        )}
      </Grid2>

      <Grid2 container>
        {/* Left Grid: Video preview */}
        <Grid2 size={{ xs: 12, md: 7 }} sx={{ mb: { xs: 1, md: 0 } }}>
          <Box sx={{ pr: { md: 2 } }}>
            <LocalVideoPreview identity={username} />
          </Box>
          <Grid2 container sx={{ mt: { xs: 1, md: 0 }, justifyContent: { xs: 'space-around', md: 'flex-start' } }}>
            <ToggleAudioButton sx={{ display: { md: 'none' }, py: 1, m: 0 }} disabled={disableButtons} />
            <ToggleVideoButton sx={{ display: { md: 'none' }, py: 1, m: 0 }} disabled={disableButtons} />
            <Box sx={{ mt: { md: 2 } }}>
              <SettingsMenu sx={{ py: 1, m: 0 }} />
            </Box>
          </Grid2>
        </Grid2>

        {/* Right Grid */}
        <Grid2 container size={{ xs: 12, md: 5 }} sx={{ flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%' }}>
            <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
            <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />
          </Box>
          <Box sx={{ width: '100%' }}>
            {!!password && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ width: '100%', mb: 1 }}
                  onClick={() => setShowInviteDialog(true)}
                >
                  Invite & Join
                </Button>
                <InviteDialog
                  open={showInviteDialog}
                  onDialogClose={() => setShowInviteDialog(false)}
                  deviceSelectionScreen={true}
                  connect={handleJoinRoom}
                  shouldRecord={shouldRecord}
                />
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              data-cy-join-now
              onClick={handleJoinClick}
              disabled={disableButtons}
              fullWidth
            >
              Join Now
            </Button>
            {/* Noise Cancellation */}
            {isKrispInstalled && (
              <Grid2 container direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ marginRight: '0.3em' }}>
                    Noise Cancellation
                  </Typography>
                  <Tooltip
                    title="Suppress background noise from your microphone"
                    leaveDelay={250}
                    leaveTouchDelay={15000}
                    enterTouchDelay={0}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InfoIconOutlined />
                    </Box>
                  </Tooltip>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!isKrispEnabled}
                      checkedIcon={<SmallCheckIcon />}
                      disableRipple={true}
                      onClick={toggleKrisp}
                    />
                  }
                  label={isKrispEnabled ? 'Enabled' : 'Disabled'}
                  style={{ marginRight: 0 }}
                  // Prevents <Switch /> from being temporarily enabled (and then quickly disabled) in unsupported browsers after
                  // isAcquiringLocalTracks becomes false:
                  disabled={isKrispEnabled && isAcquiringLocalTracks}
                />
              </Grid2>
            )}
          </Box>
        </Grid2>
      </Grid2>
    </>
  );
}
